import React, { useState } from 'react';
import Label from '../../yourProperties/Label';
import DotIcon from '../../../components/DotIcon';
import { CalendarEvent } from '../../../../types/calendar/calendarTypes';
import {
    IContractListState,
    RATE_SCOPE_MODEL_KEY_ICON,
} from '../../../../types/contracts/contractTypes';
import { useHistory } from 'react-router-dom';
import encodeBase64 from '../../../../utils/encodeBase64';
import IconButton from '../../../../muiComponents/IconButton';
import { Popover } from '@mui/material';
import SectionTitle from '../../yourProperties/Title';
import Tooltip from '../../../../muiComponents/Tooltip';
import {
    doubleMD_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../utils/commonVariables/grid';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import Icon from '../../../../muiComponents/Icon';
import Title from '../../../components/Title';
import { commonIcons } from '../../../../utils/commonVariables/commonIcons';
import useTranslate from '../../../../customHooks/useTranslate';
import routes from '../../../../utils/commonVariables/routes';
import Tag from '../../../../muiComponents/Tag';
import { RATE_SCOPE_MODEL_KEY_TAG_COLOR } from '../../../../utils/contractUtils/contractUtils';

interface EventProps {
    event: CalendarEvent<IContractListState>;
    idHoveredEvent: string | null;
    hoverEvent: (eventId: string) => void;
    unhoverEvent: () => void;
}

const ContractsEvent = ({
    event,
    idHoveredEvent,
    hoverEvent,
    unhoverEvent,
}: EventProps) => {
    const history = useHistory();
    const translate = useTranslate();
    const {
        Id,
        Enabled,
        Name,
        SupplierHotel,
        IdSupplierHotel,
        IdSupplierHotelRatePlan,
        SupplierHotelRatePlan,
        IdAgency,
        Agency,
        IdRateScopeModel,
        RateScopeModel,
        RateScopeModelKey,
    } = event.extendedProps;

    const rateScopeIconName =
        RATE_SCOPE_MODEL_KEY_ICON[
            RateScopeModelKey as keyof typeof RATE_SCOPE_MODEL_KEY_ICON
        ];
    const rateScopeTagColor =
        RATE_SCOPE_MODEL_KEY_TAG_COLOR[
            RateScopeModelKey as keyof typeof RATE_SCOPE_MODEL_KEY_TAG_COLOR
        ];

    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const openDetail = () => {
        setAnchor(null);
        const currentPath = history.location.pathname;
        history.push(`${currentPath}/${encodeBase64(Id)}`);
    };

    return (
        <>
            <div
                className={`overflow-hidden flex flex-between gap-5 flex-align-center border-radius-4 cursor-pointer padding-4 ${
                    idHoveredEvent === event.id
                        ? 'fc__hovered-event'
                        : 'border-color'
                }`}
                style={{ height: '30px', maxWidth: '100%' }}
                onClick={openDetail}
                onMouseEnter={() => hoverEvent(event.id)}
                onMouseLeave={unhoverEvent}
            >
                <div className='flex gap-5 flex-align-center overflow-hidden'>
                    <DotIcon truthy={Enabled} falsy={!Enabled} />
                    <Label text={Name} translated={false} />
                </div>
                <IconButton
                    iconName={anchor ? 'VisibilityOff' : 'Visibility'}
                    onClick={(
                        iconEvent: React.MouseEvent<
                            HTMLButtonElement,
                            MouseEvent
                        >
                    ) => {
                        iconEvent.stopPropagation();
                        setAnchor(anchor ? null : iconEvent.currentTarget);
                    }}
                    style={{ padding: '4px' }}
                    iconProps={{ style: { fontSize: '16px' } }}
                />
            </div>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    paper: {
                        borderRadius: '16px',
                    },
                }}
            >
                <div
                    style={{ width: '600px' }}
                    className='flex-column gap-20 padding20'
                >
                    <div className='flex flex-between flex-align-baseline padding-inline15 gap-10'>
                        <SectionTitle
                            className='text-secondary-color multiline-overflow__2'
                            text={Name}
                            translated={false}
                        />
                        <div className='flex gap-20 flex-align-center'>
                            <Tooltip
                                title={translate(
                                    Enabled ? 'enabled' : 'disabled'
                                )}
                            >
                                <span>
                                    <DotIcon
                                        truthy={Enabled}
                                        falsy={!Enabled}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </div>

                    <Grid
                        style={{ width: 'auto', margin: 0 }}
                        className='border-color-block'
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                    <Icon
                                        iconName={commonIcons.HOTEL}
                                        className='text-secondary-700'
                                    />
                                    <Title className='text-capitalize'>
                                        {translate('hotel')}
                                    </Title>
                                </div>
                                <Tooltip titleText='navigate_to_hotel_detail'>
                                    <p
                                        className='link-onhover cursor-pointer '
                                        onClick={() => {
                                            const route = `${routes.propertyDetail(
                                                IdSupplierHotel
                                            )}`;
                                            window.open(route, route);
                                        }}
                                    >
                                        {SupplierHotel}
                                    </p>
                                </Tooltip>
                            </div>
                        </GridChild>
                        {IdAgency ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='StorefrontOutlined'
                                            className='text-secondary-700'
                                        />
                                        <Title className='text-capitalize'>
                                            {translate('agency')}
                                        </Title>
                                    </div>
                                    <p>
                                        {Agency || translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}

                        {IdSupplierHotelRatePlan ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <div className='flex flex-align-center gap-5 text-overflow-ellipsis'>
                                        <Icon
                                            iconName='SellOutlined'
                                            className='text-secondary-700'
                                        />
                                        <Title className='text-capitalize'>
                                            {translate('rate_plan')}
                                        </Title>
                                    </div>
                                    <p>
                                        {SupplierHotelRatePlan ||
                                            translate('not_specified')}
                                    </p>
                                </div>
                            </GridChild>
                        ) : null}
                        {IdRateScopeModel ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <div className='flex-column gap-5'>
                                    <Title className='text-capitalize'>
                                        {translate('rate_scope_model')}
                                    </Title>
                                    <Tooltip titleText='rate_scope_model'>
                                        <span>
                                            <Tag
                                                color={rateScopeTagColor}
                                                text={RateScopeModel}
                                                startIcon={rateScopeIconName}
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            </GridChild>
                        ) : null}
                    </Grid>

                    <div
                        className='padding-inline15 gap-10 flex flex-baseline text-primary-400 cursor-pointer'
                        onClick={openDetail}
                    >
                        <Icon iconName='Description' />
                        {translate('go_to_contract_detail')}
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default ContractsEvent;
